import { WorkflowWorkflowType } from '@calm/evexia-web-sdk';
import createWorkflowApi from '@rhg/libs/api/create-workflow-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

interface WorkflowPreviousStepParameters {
    workflowType?: WorkflowWorkflowType;
    workflowDefnId?: string | undefined;
    acceptLanguage?: string;
    userId?: string;
}

export const workflowPreviousStepQueryOptions = {
    queryKey: ({
        workflowDefnId,
        recipientId,
        acceptLanguage,
        userId,
    }: {
        workflowDefnId: string | undefined;
        recipientId: number;
        acceptLanguage?: string;
        userId?: string;
    }) => {
        return [
            queryKeys.workflowPreviousStepByDefnId,
            recipientId,
            workflowDefnId,
            acceptLanguage,
            userId,
        ];
    },
    queryFn: async ({
        getAccessTokenSilently,
        circleAuthorizationToken,
        recipientId,
        workflowDefnId,
        acceptLanguage,
        userId,
    }: {
        getAccessTokenSilently: () => Promise<string>;
        circleAuthorizationToken: string;
        recipientId: number;
        workflowDefnId: string;
        acceptLanguage?: string;
        userId?: string;
    }) => {
        const workflowApi = createWorkflowApi(getAccessTokenSilently);

        const response = await workflowApi.v1GetPreviousStep({
            workflowDefnId,
            circleAuthorizationToken,
            recipientId,
            acceptLanguage,
            userId,
        });

        return response.data;
    },
    queryOptions: ({
        workflowDefnId,
        recipientId,
        circleAuthorizationToken,
        isAuthenticated,
    }: {
        workflowDefnId: string;
        recipientId: number;
        circleAuthorizationToken: string;
        isAuthenticated: boolean;
    }) => {
        return {
            enabled: !!(
                isAuthenticated &&
                workflowDefnId &&
                recipientId &&
                circleAuthorizationToken
            ),
        };
    },
};

export function useFetchWorkflowPreviousStep() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle } = useActiveCircle();
    const { queryFn } = workflowPreviousStepQueryOptions;
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            workflowDefnId,
            acceptLanguage,
            userId,
        }: WorkflowPreviousStepParameters) => {
            return queryFn({
                getAccessTokenSilently,
                workflowDefnId: workflowDefnId!,
                recipientId: activeCircle!.recipient.id,
                circleAuthorizationToken: activeCircle?.authorizationJWT ?? '',
                acceptLanguage,
                userId,
            });
        },
        onSuccess: async (response, { workflowDefnId, workflowType }) => {
            if (response) {
                // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
                await Promise.all([
                    invalidateQueryKey(
                        workflowByDefnIdForRecipientQueryOptions.queryKey({
                            workflowDefnId,
                            recipientId: activeCircle!.recipient.id,
                        })
                    ),
                    invalidateQueryKey(
                        workflowByTypeForRecipientQueryOptions.queryKey({
                            workflowType,
                            recipientId: activeCircle!.recipient.id,
                        })
                    ),
                ]);
            }
        },
    });
    return {
        fetchWorkflowPreviousStep: mutate,
        fetchWorkflowPreviousStepAsync: mutateAsync,
        ...otherProps,
    };
}
